/**
 * `<KissSoundSubscriber>` — plays the `/kiss` emote's sound on every connected client when a
 * genuinely NEW emote lands. Renders nothing.
 *
 * **Placement matters.** Mounted once as a peer of `<DiceFeedSubscriber>` in
 * `GameSessionProvider` — a sibling of the route tree, **not** inside COMMS.app's window. The
 * sound has to play on everybody's machine, the sender included, and `CommsApp` only renders
 * while its window is open — which is **local `useState`**, so the window is closed again after
 * every reload. A trigger living inside `CommsApp` would silently never fire for anyone who
 * hasn't opened COMMS, which on a fresh reload is everyone. `GameSessionProvider` already owns
 * the one `/ws/game` socket and the `feed`/`feedBaselineTs` this reads, and is mounted above the
 * window manager.
 *
 * **Distinguishing live from backfilled** (see `./emoteFreshness.ts` for the full mechanism):
 *  1. Only `kind: "emote"` entries can trigger the sound — chat/system/roll entries are ignored.
 *  2. No backfill flurry: freshness is judged against `feedBaselineTs` (the max `ts` of the
 *     welcome backfill that seeded the reload/join), never anything observed at mount, via
 *     {@link selectFreshEmotes}. A reload with an existing kiss in the feed sounds nothing; a
 *     genuinely new live `/kiss` sounds exactly once.
 *
 * Playback goes through {@link playKissSound} — the one chokepoint.
 */
import { useEffect, useRef } from "react";
import type { FeedEntry } from "@lancer-vtt/shared";
import { playKissSound } from "./kissSound";
import { selectFreshEmotes } from "./emoteFreshness";

export function KissSoundSubscriber({ feed, feedBaselineTs }: { feed: readonly FeedEntry[]; feedBaselineTs: number }) {
  // The freshness high-water mark. Seeded from `feedBaselineTs` (the welcome backfill's own max
  // `ts`) rather than anything observed at mount — see the file header. A ref because it must
  // survive across renders without itself triggering one, and is intentionally NOT the effect's
  // only dependency (see the sync effect below).
  const highWaterRef = useRef<number>(feedBaselineTs);

  // Re-seed (raise) the high-water mark whenever the backfill baseline changes — a fresh join or
  // a mid-session reconnect. `Math.max` guards against ever LOWERING it (a defensive floor; in
  // practice `feedBaselineTs` is itself non-decreasing across welcomes).
  useEffect(() => {
    highWaterRef.current = Math.max(highWaterRef.current, feedBaselineTs);
  }, [feedBaselineTs]);

  useEffect(() => {
    const { emotes, nextHighWater } = selectFreshEmotes(feed, highWaterRef.current);
    for (const _emote of emotes) playKissSound();
    highWaterRef.current = nextHighWater;
  }, [feed]);

  return null;
}
